// Always-visible "Active: <Center>" pill in the global top-right corner.
// Helps multi-center CMs avoid the classic "I edited the wrong center"
// mistake. Hidden for single-center users / non-logged-in / when there's
// no active center set yet.

import { useEffect, useState } from 'react';
import axios from 'axios';
import { Building2 } from 'lucide-react';
import { ACTIVE_CENTER_STORAGE_KEY } from '../../utils/activeCenter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ActiveCenterPill() {
  const [label, setLabel] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
        const cids = me.data?.center_ids || [];
        // Single-center users don't need a reminder.
        if (cids.length < 2) return;
        const rawActiveId = localStorage.getItem(ACTIVE_CENTER_STORAGE_KEY) || me.data?.center_id;
        // iter 407c — '__all__' is the "All centers" sentinel; treat as unset.
        const activeId = rawActiveId === '__all__' ? null : rawActiveId;
        if (!activeId) { if (!cancelled) setLabel('All centers'); return; }
        const centers = await axios.get(`${API_URL}/api/admin/centers`, { withCredentials: true })
          .then((r) => Array.isArray(r.data) ? r.data : (r.data?.centers || []))
          .catch(() => []);
        const match = centers.find((c) => c.center_id === activeId);
        if (!cancelled) setLabel(match?.name || activeId);
      } catch (_e) { /* silent — not logged in / endpoint unavailable */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!label) return null;
  return (
    <div
      className="fixed top-3 right-3 z-50 inline-flex items-center gap-1.5 bg-cyan-500/15 border border-cyan-500/40 text-cyan-200 text-[11px] font-medium px-2.5 py-1 rounded-full backdrop-blur-sm shadow-sm select-none"
      data-testid="active-center-pill"
      title="Currently active center context — switch in the dashboard topbar"
    >
      <Building2 className="w-3 h-3" />
      <span className="max-w-[180px] truncate">Active: {label}</span>
    </div>
  );
}
